import { createEvent, createStore } from 'effector';
import type { CameraConfig } from '../cameraConfig';
import { defaultCameraConfig } from '../cameraConfig';
import type { CollisionConfig } from '../scene/collision/CollisionSystem';
import { defaultCollisionConfig } from '../scene/collision/CollisionSystem';
import type { DevConfig } from '../devConfig';
import { defaultDevConfig } from '../devConfig';
import type { PoiConfig } from '../poiConfig';
import { defaultPoiConfig } from '../poiConfig';
import type { TellerAssistantConfig } from '../tellerAssistantConfig';
import { defaultTellerAssistantConfig } from '../tellerAssistantConfig';
import { DEFAULT_MUSIC_TRACK_ID } from '../audio/musicTracks';
import { DEFAULT_ASSISTANT_VOICE_NAME } from '../audio/assistantVoices';

const APP_CONFIG_STORAGE_KEY = 'pilgrim.appConfig';

export interface AppConfig {
  camera: CameraConfig;
  collision: CollisionConfig;
  dev: DevConfig;
  poi: PoiConfig;
  audio: AudioConfig;
  tellerAssistant: TellerAssistantConfig;
}

export interface AudioConfig {
  soundOn: boolean;
  musicOn: boolean;
  voiceOn: boolean;
  musicTrackId: string;
  musicVolume: number;
  voiceVolume: number;
  assistantVoiceName: string;
}

const defaultAudioConfig: AudioConfig = {
  soundOn: false,
  musicOn: false,
  voiceOn: false,
  musicTrackId: DEFAULT_MUSIC_TRACK_ID,
  musicVolume: 1,
  voiceVolume: 1,
  assistantVoiceName: DEFAULT_ASSISTANT_VOICE_NAME,
};

const createDefaultAppConfig = (): AppConfig => ({
  camera: { ...defaultCameraConfig },
  collision: { ...defaultCollisionConfig },
  dev: { ...defaultDevConfig },
  poi: { ...defaultPoiConfig },
  audio: { ...defaultAudioConfig },
  tellerAssistant: { ...defaultTellerAssistantConfig },
});

const clampVolume = (value: number) =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;

const normalizeAudioConfig = (audio: AudioConfig): AudioConfig => {
  const next = {
    ...audio,
    musicVolume: clampVolume(audio.musicVolume),
    voiceVolume: clampVolume(audio.voiceVolume),
  };

  if (next.musicVolume <= 0) {
    next.musicOn = false;
  }

  if (next.voiceVolume <= 0) {
    next.voiceOn = false;
  }

  if (!next.musicOn && !next.voiceOn) {
    next.soundOn = false;
  }

  return next;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeDevConfig = (state: DevConfig, patch: Partial<DevConfig>): DevConfig => ({
  ...state,
  ...patch,
});

const readStoredAppConfig = (): Partial<AppConfig> | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(APP_CONFIG_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return isRecord(parsed) ? (parsed as Partial<AppConfig>) : null;
  } catch {
    return null;
  }
};

const persistAppConfig = (state: AppConfig): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors to avoid breaking the app in private mode.
  }
};

const mergeAppConfig = (
  state: AppConfig,
  patch: Partial<AppConfig>
): AppConfig => {
  const nextCamera = isRecord(patch.camera)
    ? { ...state.camera, ...(patch.camera as Partial<CameraConfig>) }
    : state.camera;
  const nextCollision = isRecord(patch.collision)
    ? { ...state.collision, ...(patch.collision as Partial<CollisionConfig>) }
    : state.collision;
  const nextDev = isRecord(patch.dev)
    ? mergeDevConfig(state.dev, patch.dev as Partial<DevConfig>)
    : state.dev;
  const nextPoi = isRecord(patch.poi)
    ? { ...state.poi, ...(patch.poi as Partial<PoiConfig>) }
    : state.poi;
  const nextAudio = isRecord(patch.audio)
    ? normalizeAudioConfig({
        ...state.audio,
        ...(patch.audio as Partial<AudioConfig>),
      })
    : state.audio;
  const patchTellerAssistant = patch.tellerAssistant as Partial<TellerAssistantConfig> | undefined;
  const patchTransform = patchTellerAssistant?.transform;
  const nextTellerAssistant = isRecord(patch.tellerAssistant)
    ? {
        ...state.tellerAssistant,
        ...(patchTellerAssistant),
        transform: {
          ...defaultTellerAssistantConfig.transform,
          ...state.tellerAssistant.transform,
          ...(isRecord(patchTransform) ? patchTransform : {}),
          desktop: {
            ...defaultTellerAssistantConfig.transform.desktop,
            ...state.tellerAssistant.transform?.desktop,
            ...(isRecord(patchTransform?.desktop) ? patchTransform?.desktop : {}),
          },
          mobile: {
            ...defaultTellerAssistantConfig.transform.mobile,
            ...state.tellerAssistant.transform?.mobile,
            ...(isRecord(patchTransform?.mobile) ? patchTransform?.mobile : {}),
          },
        },
        lighting: isRecord(patchTellerAssistant?.lighting)
          ? {
              ...state.tellerAssistant.lighting,
              ...(patchTellerAssistant?.lighting as Partial<TellerAssistantConfig['lighting']>),
              simple: {
                ...defaultTellerAssistantConfig.lighting.simple,
                ...state.tellerAssistant.lighting.simple,
                ...(isRecord(patchTellerAssistant?.lighting?.simple)
                  ? (patchTellerAssistant?.lighting?.simple as Partial<TellerAssistantConfig['lighting']['simple']>)
                  : {}),
              },
              env: {
                ...defaultTellerAssistantConfig.lighting.env,
                ...state.tellerAssistant.lighting.env,
                ...(isRecord(patchTellerAssistant?.lighting?.env)
                  ? (patchTellerAssistant?.lighting?.env as Partial<TellerAssistantConfig['lighting']['env']>)
                  : {}),
              },
            }
          : state.tellerAssistant.lighting,
      }
    : state.tellerAssistant;

  return {
    camera: nextCamera,
    collision: nextCollision,
    dev: nextDev,
    poi: nextPoi,
    audio: nextAudio,
    tellerAssistant: nextTellerAssistant,
  };
};

export const updateAppConfig = createEvent<Partial<AppConfig>>();
export const updateCameraConfig = createEvent<Partial<CameraConfig>>();
export const updateCollisionConfig = createEvent<Partial<CollisionConfig>>();
export const updateDevConfig = createEvent<Partial<DevConfig>>();
export const updatePoiConfig = createEvent<Partial<PoiConfig>>();
export const updateAudioConfig = createEvent<Partial<AudioConfig>>();
export const updateTellerAssistantConfig = createEvent<Partial<TellerAssistantConfig>>();
export const resetAppConfig = createEvent();

const initialConfig = (() => {
  const defaults = createDefaultAppConfig();
  const stored = readStoredAppConfig();
  return stored ? mergeAppConfig(defaults, stored) : defaults;
})();

export const $appConfig = createStore<AppConfig>(initialConfig)
  .on(updateAppConfig, (state, patch) => mergeAppConfig(state, patch))
  .on(updateCameraConfig, (state, patch) => ({
    ...state,
    camera: { ...state.camera, ...patch },
  }))
  .on(updateCollisionConfig, (state, patch) => ({
    ...state,
    collision: { ...state.collision, ...patch },
  }))
  .on(updateDevConfig, (state, patch) => ({
    ...state,
    dev: { ...state.dev, ...patch },
  }))
  .on(updatePoiConfig, (state, patch) => ({
    ...state,
    poi: { ...state.poi, ...patch },
  }))
  .on(updateAudioConfig, (state, patch) => {
    const nextAudio = normalizeAudioConfig({ ...state.audio, ...patch });

    return {
      ...state,
      audio: nextAudio,
    };
  })
  .on(updateTellerAssistantConfig, (state, patch) => ({
    ...state,
    tellerAssistant: { ...state.tellerAssistant, ...patch },
  }))
  .on(resetAppConfig, () => createDefaultAppConfig());

$appConfig.watch((state) => {
  persistAppConfig(state);
});

export const $cameraConfig = $appConfig.map((config) => config.camera);
export const $collisionConfig = $appConfig.map((config) => config.collision);
export const $devConfig = $appConfig.map((config) => config.dev);
export const $poiConfig = $appConfig.map((config) => config.poi);
export const $audioConfig = $appConfig.map((config) => config.audio);

// Preview Model waves configuration (not persisted to localStorage)
export interface PreviewModelConfig {
  wavesCenter: { x: number; y: number; z: number };
  wavesMinDistance: number;    // 0-1 - minimum distance from center (as fraction of model size)
  wavesAmplitude: number;      // 1-10 - wave width
  wavesPeriod: number;         // 1-10 - number of simultaneous waves
  wavesSpeed: number;          // speed multiplier
  wavesColorIntensity: number; // 0-10 - color intensity
  wavesDisplacement: number;   // 0-0.1 - displacement as percentage
}

export const defaultPreviewModelConfig: PreviewModelConfig = {
  wavesCenter: { x: 0, y: 0, z: 0 },
  wavesMinDistance: 0,
  wavesAmplitude: 3.0,
  wavesPeriod: 1.0,
  wavesSpeed: 1.0,
  wavesColorIntensity: 5.0,
  wavesDisplacement: 0.01,
};

export const updatePreviewModelConfig = createEvent<Partial<PreviewModelConfig>>();
export const resetPreviewModelConfig = createEvent();

export const $previewModelConfig = createStore<PreviewModelConfig>(defaultPreviewModelConfig)
  .on(updatePreviewModelConfig, (state, patch) => ({
    ...state,
    ...patch,
    wavesCenter: patch.wavesCenter
      ? { ...state.wavesCenter, ...patch.wavesCenter }
      : state.wavesCenter,
  }))
  .on(resetPreviewModelConfig, () => defaultPreviewModelConfig);

// Background configuration (not persisted to localStorage)
export interface BackgroundConfigState {
  color: number;
  skyboxEnabled: boolean;
  skyboxUrl: string;            // absolute URL for skybox texture (empty = use scene default)
  skyboxIntensity: number;      // 0-1
  skyboxBlur: number;           // 0-1
  skyboxRotation: [number, number, number]; // degrees
}

export const defaultBackgroundConfig: BackgroundConfigState = {
  color: 0x0f0f13,
  skyboxEnabled: true,
  skyboxUrl: '',
  skyboxIntensity: 1.0,
  skyboxBlur: 0.0,
  skyboxRotation: [0, 0, 0],
};

export const updateBackgroundConfig = createEvent<Partial<BackgroundConfigState>>();

export const $backgroundConfig = createStore<BackgroundConfigState>(defaultBackgroundConfig)
  .on(updateBackgroundConfig, (state, patch) => ({
    ...state,
    ...patch,
    skyboxRotation: patch.skyboxRotation ?? state.skyboxRotation,
  }));

export const $tellerAssistantConfig = $appConfig.map((config) => config.tellerAssistant);
